import {
  ActivePeriod,
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  FormField,
  FormInput,
  FormSelect,
  FormTableDateRangeField,
  getDatePeriodString,
  getDatePickerMode,
  getDayjs,
  getFormTableFieldName,
  getFormTableFieldPath,
  getGroupDescriptionFrequency,
  GroupDescriptionOption,
  GroupDescriptionOptions,
  GroupDescriptionRef,
  isDisabledDate,
  SystemClassifierOption,
  TanstackFormTable,
  TimeSeriesOutputType,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import { FC, memo, useMemo } from 'react';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { GroupOrDescriptionSelect } from '../../GroupOrDescriptionSelect';
import { Dayjs } from 'dayjs';
import { useFormContext, useWatch } from 'react-hook-form';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './TimeSeriesActionModal.translations.json';

const { addTranslations, t } = i18nNsFactory('time-series-action-modal');
addTranslations(translations);

type ColumnConfig<TData extends TimeSeriesOutputType> = {
  sourceDataVersionId?: {
    accessor: (row: TData) => any;
    options: SystemClassifierOption[];
  };
  confirmationStatusId?: {
    accessor: (row: TData) => any;
    options: SystemClassifierOption[];
  };
  confirmationComment?: {
    accessor: (row: TData) => any;
  };
};

type TimeSeriesOutputsTableProps<TData extends TimeSeriesOutputType> = {
  groupsWithDescriptionsOptions: GroupDescriptionOptions;
  columnConfig?: ColumnConfig<TData>;
  excludeIntermediates?: boolean;
  isLoading?: boolean;
};

export function TimeSeriesOutputsTable<TData extends TimeSeriesOutputType>({
  groupsWithDescriptionsOptions,
  columnConfig,
  isLoading,
}: TimeSeriesOutputsTableProps<TData>) {
  const columnHelper = createColumnHelper<TData>();
  const columns = useMemo(() => {
    const { sourceDataVersionId, confirmationStatusId, confirmationComment } =
      columnConfig || {};

    const cols: ColumnDef<TData, any>[] = [
      columnHelper.accessor((_rowData, index) => index + 1, {
        id: 'sequenceNumber',
        header: t('column.sequenceNumber'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: 100,
          className: 'sticky-left',
        },
      }),
      columnHelper.accessor((rowData) => rowData.groupDescriptionRef, {
        id: 'groupDescriptionRef',
        header: t('column.name'),
        meta: { width: '20ch', className: 'sticky-left' },
        enableColumnFilter: false,
        enableSorting: false,
        cell: (props) => (
          <GroupDescriptionCell
            {...props}
            options={groupsWithDescriptionsOptions}
            sourceDataVersionOptions={sourceDataVersionId?.options || []}
            isLoading={isLoading}
          />
        ),
      }),
      columnHelper.accessor(
        (rowData) =>
          getDatePeriodString(...(rowData.datePeriod ?? [])) || undefined,
        {
          id: 'datePeriod',
          header: t('column.datePeriod'),
          cell: (props) => (
            <ValueDatePeriodCell
              {...props}
              options={groupsWithDescriptionsOptions}
            />
          ),
          meta: { width: 220 },
          enableColumnFilter: false,
          enableSorting: false,
        },
      ),
    ];

    if (sourceDataVersionId) {
      cols.push(
        columnHelper.accessor(sourceDataVersionId.accessor, {
          id: 'sourceDataVersionId',
          header: t('column.sourceDataVersion'),
          cell: (props) => (
            <FormSelectCell {...props} options={sourceDataVersionId.options} />
          ),
          meta: { width: '20ch' },
          enableColumnFilter: false,
          enableSorting: false,
        }),
      );
    }

    if (confirmationStatusId) {
      cols.push(
        columnHelper.accessor(confirmationStatusId.accessor, {
          id: 'statusId',
          header: t('column.confirmationStatus'),
          cell: (props) => (
            <FormSelectCell {...props} options={confirmationStatusId.options} />
          ),
          meta: { width: '20ch' },
          enableColumnFilter: false,
          enableSorting: false,
        }),
      );
    }

    if (confirmationComment) {
      cols.push(
        columnHelper.accessor(confirmationComment.accessor, {
          id: 'comment',
          header: t('column.confirmationComment'),
          cell: CommentCell,
          meta: { minWidth: '20ch' },
          enableColumnFilter: false,
          enableSorting: false,
        }),
      );
    }

    cols.push(
      columnHelper.accessor('operations' as any, {
        header: () => (
          <Flex justify="center" gap="small">
            <CreateFormTableRowButton
              autoFocusField="id"
              defaultRowValues={{
                id: null,
                datePeriod: null,
                comment: '',
              }}
            />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => (
          <Flex justify="center" gap="small">
            <DeleteFormTableRowButton context={props} />
          </Flex>
        ),
        meta: {
          width: 88,
          className: 'sticky-right',
        },
      }),
    );

    return cols;
  }, [columnConfig, columnHelper, groupsWithDescriptionsOptions, isLoading]);

  return (
    <TanstackFormTable
      name="outputs"
      columns={columns}
      persistColumnStyle={false}
      columnResizeDisabled
    />
  );
}

const CommentCell: FC<CellContext<any, any>> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField size="small" name={name}>
      <FormInput size="small" name={name} autoFocus maxLength={2000} />
    </FormField>
  );
});

const FormSelectCell: FC<
  CellContext<any, any> & {
    options: SystemClassifierOption[];
  }
> = memo((props) => {
  const { options } = props;
  const name = getFormTableFieldName(props);
  return (
    <FormField
      size="small"
      name={name}
      preview={{
        render: (value) => options.find((o) => o.value === value)?.label,
      }}
    >
      <FormSelect
        size="small"
        name={name}
        options={options}
        autoFocus
        defaultOpen
      />
    </FormField>
  );
});

const ValueDatePeriodCell: FC<
  CellContext<any, any> & {
    options: GroupDescriptionOptions;
  }
> = memo((props) => {
  const { options } = props;
  const path = getFormTableFieldPath(props);
  const basePath = path.slice(0, -1);
  const groupDescriptionRef = useWatch({
    name: [...basePath, 'groupDescriptionRef'].join('.'),
  });

  const activePeriods: ActivePeriod[] = getGroupDescriptionActivePeriods(
    groupDescriptionRef,
    options,
  );
  const validPeriods = activePeriods.map(
    (period) =>
      [getDayjs(period.validFrom), getDayjs(period.validTo)] as [
        Dayjs | null,
        Dayjs | null,
      ],
  );

  const frequency = getGroupDescriptionFrequency(groupDescriptionRef, options);

  return (
    <FormTableDateRangeField
      {...props}
      disabledDate={(date) => isDisabledDate(date, validPeriods)}
      allowEmpty={[false, false]}
      picker={getDatePickerMode(frequency)}
      endOfPeriod
    />
  );
});

const GroupDescriptionCell: FC<
  CellContext<any, any> & {
    options: GroupDescriptionOptions;
    sourceDataVersionOptions: SystemClassifierOption[];
    isLoading?: boolean;
  }
> = memo((props) => {
  const { isLoading, options, sourceDataVersionOptions } = props;
  const name = getFormTableFieldName(props);
  const path = getFormTableFieldPath(props);
  const basePath = path.slice(0, -1);
  const { setValue } = useFormContext();

  const handleGroupDescriptionSelect = useMemo(
    () => (value: number | GroupDescriptionRef) => {
      // Reset datePeriod
      setValue([...basePath, 'datePeriod'].join('.'), null);

      // Set default sourceDataVersionId if applicable
      if (sourceDataVersionOptions.length > 0) {
        const defaultSourceDataVersionId = getDefaultSourceDataVersionId(
          options.flat(),
          sourceDataVersionOptions,
          value,
        );
        setValue(
          [...basePath, 'sourceDataVersionId'].join('.'),
          defaultSourceDataVersionId,
        );
      }
    },
    [setValue, basePath, options, sourceDataVersionOptions],
  );

  return (
    <FormField
      name={name}
      size="small"
      preview={{
        render: (value) => getGroupDescriptionLabel(value, options),
      }}
    >
      <FormSelect size="small" name={name} autoFocus defaultOpen>
        <GroupOrDescriptionSelect
          isLoading={isLoading}
          options={options}
          withGroupDescriptions
          onSelect={handleGroupDescriptionSelect}
        />
      </FormSelect>
    </FormField>
  );
});

const getGroupDescriptionLabel = (
  ref: string,
  options: GroupDescriptionOptions,
) => options.flat().find((option) => option.value.toString() === ref)?.label;

const getGroupDescriptionActivePeriods = (
  ref: string,
  options: GroupDescriptionOptions,
) =>
  options.flat().find((option) => option.value.toString() === ref)
    ?.activePeriods ?? [];

export const getDefaultSourceDataVersionId = (
  groupDescriptionOptions: GroupDescriptionOption[],
  sourceDataVersionOptions: SystemClassifierOption[],
  groupDescriptionRef: number | GroupDescriptionRef,
) => {
  const groupDescriptionOption = groupDescriptionOptions.find(
    (option) => option.value.toString() === groupDescriptionRef,
  );
  if (!groupDescriptionOption) return undefined;

  const value = sourceDataVersionOptions.find((option) =>
    groupDescriptionOption.outputType === 'REPORT'
      ? option.code === 'PUBLISHING'
      : option.code === 'LATEST',
  )?.value;
  return value !== undefined ? Number(value) : undefined;
};
